<script setup>
import { ref, onMounted,computed } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
import Swal from "sweetalert2";
import { studentLists, migrateStudent, fetchClassVerificationStatus } from "@/services/apiServices";
import EditStudent from "@/components/Apps/ClassDetails/EditStudent.vue";
import addStudent from "@/components/Apps/ClassDetails/addStudent.vue";
import UpdateStudent from "@/components/Apps/ClassDetails/UpdateStudent.vue";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import Papa from 'papaparse';
import { apiConfig } from "@/config/apiConfig";
import banner1 from "@/assets/images/administrator.jpg";
import noImage from "@/assets/images/image_not_available-removebg-preview (1).png"
import { useTheme } from '@/composables/useThemes'
const { isDarkTheme } = useTheme()

const route = useRoute();
const fetchedStudents = ref([]);
const search = ref("");
const isEditDialogOpen = ref(false);
const loading = ref(true);
const selectedStudent = ref(null);
const isVerified = ref(false);
const classIdFromRoute = parseInt(route.params.id);

const itemsPerPage = ref(10);
 const computedItemsPerPage = computed(() => {
  return itemsPerPage.value === 'All' ? -1 : Number(itemsPerPage.value)
});
const headers = ref([
  { align: "start", key: "roll_symbol_no", sortable: true, title: "ROLL" },
  { key: "name", sortable: true, title: "NAME" },
  // { key: "class_name", sortable: true, title: "CLASS" },
  // { key: "section", sortable: true, title: "SECTION" },
  { key: "birth_date", sortable: true, title: "DOB" },
  // { key: "gender", sortable: true, title: "GENDER" },
  { key: "parent_name", sortable: true, title: "Parent's Name" },
  { key: "contact", sortable: true, title: "CONTACT" },
  { key: "address", sortable: true, title: "ADDRESS" },
  { key: "actions", sortable: false, title: "ACTION" },
]);


const getStudentsForClass = async () => {
  try {
    const response = await studentLists(classIdFromRoute);
    if (response) {
      fetchedStudents.value = response;
    } else {
      console.warn("No data returned from the API");
    }
  } catch (error) {
    console.error("Error fetching students:", error);
  } finally {
    loading.value = false;
  }
};


const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const handleStudentAdded = (newStudent) => {
  fetchedStudents.value.push(newStudent.data);
};

const handleVerifyStudents = async () => {
  try {
    loading.value = true;
    const formData = new FormData();
    formData.append("class_info", classIdFromRoute);
    formData.append("is_verified", !isVerified.value);

    await fetchClassVerificationStatus(classIdFromRoute, formData);

    isVerified.value = !isVerified.value;

    Swal.fire({
      icon: "success",
      title: `Students ${isVerified.value ? "verified" : "unverified"} successfully`,
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: isDarkTheme.value ? '#333' : '#fff',
      color: isDarkTheme.value ? '#fff' : '#000',
      iconColor: isDarkTheme.value ? "#0f0" : "#28a745",
    });

  } catch (error) {
    console.error("Error verifying students:", error);
    Swal.fire({
      icon: "error",
      title: "Failed to update students",
      text: error.message,
    });
  } finally {
    loading.value = false;
  }
};

const openEditStudent = (student) => {
  selectedStudent.value = { ...student };
  isEditDialogOpen.value = true;
};

const handleStudentUpdate = (updatedStudent) => {
  const index = fetchedStudents.value.findIndex(
    (student) => student.id === updatedStudent.id
  );
  if (index !== -1) {
    fetchedStudents.value[index] = updatedStudent; // Update the student in the list
  }
  isEditDialogOpen.value = false;
};

const exportToCSV = () => {
  const csv = Papa.unparse(
    fetchedStudents.value.map((student) => {
      // Filter out the unwanted fields
      const { school, class_info, image, ...rest } = student;
      return rest; //Returns the student object without `school`, `image` and `class_info`
    })
  );
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", "students_list.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToPDF = () => {
  const doc = new jsPDF();
  const columns = [
    "Roll No",
    "Name",
    "Class",
    "DOB",
    "Parent's Name",
    "Gender",
    "Contact",
  ];

  const rows = fetchedStudents.value.map((student) => [
    student.roll_no,
    student.name,
    student.class_name,
    student.birth_date,
    student.parent_name,
    student.gender,
    student.contact,
  ]);

  autoTable(doc, {
    head: [columns],
    body: rows,
    startY: 20,
    margin: { top: 30 },
    styles: {
      fontSize: 10,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [22, 160, 133],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240],
    },
  });

  doc.save("students_list.pdf");
};

const printTable = () => {
  if (!fetchedStudents.value.length) {
    console.error("No data to print");
    return;
  }

  let tableHtml = `<table border="1" width="100%">
    <thead>
      <tr>`;

  // Exclude the 'actions' column from the header
  headers.value.forEach((header) => {
    if (header.key !== "actions") {
      tableHtml += `<th>${header.title}</th>`;
    }
  });

  tableHtml += `</tr>
    </thead>
    <tbody>`;

  // Exclude the 'actions' column from the student data
  fetchedStudents.value.forEach((student) => {
    tableHtml += `<tr>`;
    headers.value.forEach((header) => {
      if (header.key !== "actions") {
        tableHtml += `<td>${student[header.key] || ""}</td>`;
      }
    });
    tableHtml += `</tr>`;
  });

  tableHtml += `</tbody>
  </table>`;

  const w = window.open("", "", "width=800,height=600");

  w.document.write(`
    <html>
      <head>
        <title>Print Table</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; background-color: #fff; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f4f4f4; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          @media print {
            body { margin: 0; padding: 0; }
            table { width: 100%; }
            th, td { font-size: 12px; padding: 8px; }
          }
        </style>
      </head>
      <body>
        <h2>Student List</h2>
        ${tableHtml}
      </body>
    </html>
  `);

  w.document.close();

  setTimeout(() => {
    w.focus();
    w.print();
    w.close();
  }, 500);
};


const migratesStudent = async (studentId) => {
  try {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to migratesStudent ? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, migratesStudent it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      const form = new FormData();
      form.append("is_migrated", true);
      fetchedStudents.value = fetchedStudents.value.filter(
        (student) => student.id !== studentId
      );
      const response = await migrateStudent(form, studentId, classIdFromRoute);
    }
    Swal.fire({
      icon: "success",
      title: "Student migrated successfully",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: isDarkTheme.value ? '#333' : '#fff',
      color: isDarkTheme.value ? '#fff' : '#000',
      iconColor: isDarkTheme.value ? "#0f0" : "#28a745",
    });
  } catch (error) {
    console.error("Error migratesStudent:", error.message);

    Swal.fire({
      icon: "error",
      title: "Failed to migratesStudent ",
      text: error.message,
    });
  }
};

onMounted(async () => {
  try {
    await getStudentsForClass();
    const data = await fetchClassVerificationStatus(classIdFromRoute);
    isVerified.value = data.is_verified;
  } catch (error) {
    console.error("Initialization failed:", error);
  }
});

</script>

<template>
  <v-container>
    <div class="d-flex align-center justify-space-between">
      <addStudent @addedStudent="handleStudentAdded" />
      <v-btn color="primary" class="ml-5 mb-7" @click="handleVerifyStudents" :disabled="loading" dense>
        <!-- <v-icon start>{{ isVerified ? 'mdi-close' : 'mdi-check' }}</v-icon>
        {{ isVerified ? 'Unverify Students' : 'Verify Students' }} -->
        <v-icon :color="isVerified ? 'green' : 'red'" size="24">
          {{ isVerified ? 'mdi-check-decagram' : 'mdi-close-circle-outline' }}
        </v-icon>
        <span class="text-subtitle-1 font-weight-medium">
          {{ isVerified ? 'Verified' : 'Unverified' }}
        </span>
      </v-btn>
    </div>
    <v-card class="pa-4 elevation-4 rounded-lg">
      <div class="class-container">
        <!-- <v-icon>mdi-school</v-icon> -->
        <v-icon>mdi-account-group</v-icon>
        <span>
          {{ fetchedStudents.length ? fetchedStudents[0].class_name : 'N/A' }}
        </span>
      </div>

      <v-row class="px-4 align-center justify-space-between">
        <v-col cols="2" class="mr-auto d-flex align-center">
          <v-label class="mr-3" style="font-weight: 400">Show</v-label>
          <v-select v-model="itemsPerPage" :items="[10, 25, 50, 75, 100, 'All']"
 variant="outlined" hide-details
            class="w-25 v-custom-select" dense />
        </v-col>
        <v-col cols="3">
          <v-text-field v-model="search" label="Search" prepend-inner-icon="mdi-magnify" variant="outlined" hide-details
            class="w-100 v-custom-select" dense />
        </v-col>
      </v-row>
      <v-row class="pa-4">
        <v-btn color="primary" class="ml-3" dense @click="exportToCSV">
          CSV
        </v-btn>
        <v-btn color="primary" class="ml-3" dense @click="exportToPDF">
          PDF
        </v-btn>
        <v-btn color="primary" class="ml-3" dense @click="printTable">
          Print
        </v-btn>
      </v-row>
      <!-- Loading Indicator -->
      <v-row v-if="loading" align="center" justify="center" class="py-10">
        <v-progress-circular indeterminate color="primary" size="40" width="4" />
      </v-row>
      <v-data-table v-else id="studentTable" :headers="headers" :items="fetchedStudents" :items-per-page="computedItemsPerPage" :search="search"
        item-value="id" class="elevation-1 mt-4 student-list-table display-table display " dense>
        <template v-slot:[`item.name`]="{ item }">
          <v-row align="center" no-gutters>
            <v-col class="d-flex align-center pa-2" :cols="3">
              <v-img :src="item.image || noImage" class="student-image mrr-10" alt="Student Image" />
              <span>{{ item.name }}</span>
              <!-- <v-col class="pl-3" :cols="8">
              </v-col> -->
            </v-col>
          </v-row>
        </template>

        <template v-slot:item.birth_date="{ item }">
          <span>{{ item.birth_date }}</span>
          <!-- <span>{{ formatDate(item.birth_date) }}</span> -->
        </template>
        <!-- <template v-slot:item.birth_date="{ item }">
          <span>{{ item.birth_date }}</span>
        </template> -->

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-space-between">
            <v-btn icon @click="openEditStudent(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <!-- <v-btn icon @click="deleteStudent(item.id)">
              <v-icon color="red">mdi-delete</v-icon>
            </v-btn> -->
            <v-btn icon @click="migratesStudent(item.id)">
              <v-icon color="green">mdi-recycle</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:footer="{ props }">
          <v-row class="align-center">
            <v-col cols="auto">
              <span class="footer-text">Total Students:</span>
            </v-col>
            <v-col cols="auto">
              <v-data-table-footer :props="props" class="d-flex justify-start align-center" />
            </v-col>
          </v-row>
        </template>
      </v-data-table>

    </v-card>

  </v-container>

  <v-dialog v-model="isEditDialogOpen" max-width="800">
    <!--  <EditStudent v-if="selectedStudent" :studentData="selectedStudent" @updateStudent="handleStudentUpdate"
      @close="isEditDialogOpen = false" />
  </v-dialog> -->
    <UpdateStudent v-if="selectedStudent" :studentData="selectedStudent" @updateStudent="handleStudentUpdate"
      @close="isEditDialogOpen = false" />
  </v-dialog>
</template>


<style>
.class-container {
  /* display: flex;
  align-items: center;
  justify-content: center; */
  /* background: linear-gradient(to right, #7a2cbf0c, #b8a9c4, #c77dff); */
  margin: 10px 0px;
  padding: 16px;
  /* border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); */
  color: rgb(64, 156, 209);
  font-size: 1.25rem;
  font-weight: 600;
  text-shadow: 1px 1px 2px #00000055;
  letter-spacing: 0.5px;
}

.class-container span {
  margin-left: 10px;
}

.v-data-table-footer__info::before {
  content: "Total Students: ";
  margin-right: 10px;
}

</style>